const STORAGE_KEY = "sonicbox-email-login-diagnostics";
const MAX_ENTRIES = 30;

const isBrowser = () => typeof window !== "undefined";

const safeNow = () => new Date().toISOString();

const createId = () => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return `email_${window.crypto.randomUUID()}`;
  }

  return `email_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const safelyNormalize = (value) => {
  if (value === undefined || value === null) return "";

  const stringValue = String(value);

  try {
    return stringValue.normalize("NFKC");
  } catch (_error) {
    return stringValue;
  }
};

const readEntries = () => {
  if (!isBrowser()) return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const writeEntries = (entries) => {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(entries.slice(-MAX_ENTRIES))
    );
  } catch (_error) {
    // Diagnostics are best-effort only.
  }
};

const getSafeSearch = () => {
  if (!isBrowser()) return null;

  try {
    const params = new URLSearchParams(window.location.search);
    ["code", "verificationCode", "otp"].forEach((key) => {
      if (params.has(key)) {
        params.set(key, "[redacted]");
      }
    });

    const safeSearch = params.toString();
    return safeSearch ? `?${safeSearch}` : "";
  } catch (_error) {
    return "";
  }
};

export const normalizeEmailForLogin = (email) => {
  return safelyNormalize(email).trim().toLowerCase();
};

export const normalizeNotificationId = (notificationid) => {
  return safelyNormalize(notificationid).trim();
};

export const normalizeVerificationCode = (code) => {
  return safelyNormalize(code)
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\s-]+/g, "")
    .trim();
};

export const describeVerificationCodeInput = (rawCode) => {
  const raw = typeof rawCode === "string" ? rawCode : "";
  const normalized = normalizeVerificationCode(raw);

  return {
    rawLength: raw.length,
    normalizedLength: normalized.length,
    changedByNormalization: raw !== normalized,
    hadWhitespace: /\s/.test(raw),
    hadSeparators: /-/.test(raw),
    shape: normalized === ""
      ? "empty"
      : /^[0-9]+$/.test(normalized)
        ? `digits:${normalized.length}`
        : /^[a-zA-Z0-9]+$/.test(normalized)
          ? `alnum:${normalized.length}`
          : `mixed:${normalized.length}`,
  };
};

export const getAxiosErrorDetails = (error) => {
  const responseData = error?.response?.data;
  const responseMessage =
    responseData?.msg ??
    responseData?.message ??
    responseData?.error ??
    (typeof responseData === "string" ? responseData : null);

  return {
    message: responseMessage ?? error?.message ?? "Unknown error",
    httpStatus: error?.response?.status ?? null,
    httpStatusText: error?.response?.statusText ?? null,
    backendStatus: responseData?.status ?? null,
    backendCode: responseData?.code ?? responseData?.reason ?? null,
    hasResponse: !!error?.response,
    hasRequest: !!error?.request,
  };
};

export const classifyEmailLoginError = (error) => {
  const details = getAxiosErrorDetails(error);
  const message = String(details.message ?? "").toLowerCase();

  if (!details.hasResponse && details.hasRequest) return "network";
  if (details.httpStatus === 429 || message.includes("too many")) return "rate_limited";
  if (details.httpStatus >= 500) return "server_error";
  if (message.includes("expired") || message.includes("venc")) return "expired";
  if (message.includes("used") || message.includes("usad")) return "already_used";
  if (message.includes("login not found")) return "login_not_found";
  if (message.includes("notification")) return "notification_id";
  if (message.includes("missing") && message.includes("code")) return "missing_code";
  if (message.includes("invalid") || message.includes("inval")) return "invalid_code";

  return "unknown";
};

export const recordEmailLoginDiagnostic = (type, payload = {}) => {
  const entry = {
    id: payload.attemptId ?? createId(),
    type,
    occurredAt: safeNow(),
    appVersion: process.env.REACT_APP_VERSION ?? null,
    pathname: isBrowser() ? window.location.pathname : null,
    search: getSafeSearch(),
    userAgent: isBrowser() ? window.navigator.userAgent : null,
    language: isBrowser() ? window.navigator.language : null,
    online: isBrowser() ? window.navigator.onLine : null,
    ...payload,
  };

  delete entry.code;
  delete entry.verificationCode;
  delete entry.rawCode;
  delete entry.normalizedCode;

  const entries = readEntries();
  entries.push(entry);
  writeEntries(entries);

  return entry;
};

export const getEmailLoginDiagnostics = () => readEntries();

export const getLatestEmailLoginDiagnostic = () => {
  const entries = readEntries();
  return entries.length > 0 ? entries[entries.length - 1] : null;
};

export const formatEmailLoginDiagnosticsForSupport = () => {
  const entries = readEntries();

  return JSON.stringify(
    {
      generatedAt: safeNow(),
      note: "Verification codes are intentionally not included.",
      entries,
    },
    null,
    2
  );
};
